import { useActionState } from "react";
import {
  archiveRungAction,
  createRungAction,
  restoreRungAction,
  updateRungAction,
} from "@/actions/templates";
import { Button } from "@/components/Button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { SelectField, TextField } from "@/components/Field";
import { StatusBadge } from "@/components/StatusBadge";
import type { RungRow } from "@shared/dto";
import { type FormState, initialFormState } from "@/lib/formState";
import { EFFORTS } from "@shared/validation";
import styles from "./RungLadder.module.css";

// The hour ranges belong in the dropdown itself, not only in a hint beside it:
// bare bands get authored inconsistently, and inconsistent authoring is what
// breaks every filter that reads them.
const EFFORT_RANGES: Record<(typeof EFFORTS)[number], string> = {
  QUICK: "under 15 min",
  SHORT: "15-60 min",
  MEDIUM: "1-4 hours",
  LONG: "4+ hours",
};

const effortOptions = EFFORTS.map((value) => ({
  value,
  label: `${value} — ${EFFORT_RANGES[value]}`,
}));

// goal_shape pins one thing only: a THRESHOLD carries a target and a MANUAL
// carries none. Repetition is a separate axis and composes with either, so
// required days is offered at every goal type rather than gated on one.
const shapeFor = (goalType: string) => ({
  hasTarget: goalType === "THRESHOLD",
});

const toLocalInput = (value: string | null): string =>
  value === null ? "" : new Date(value).toISOString().slice(0, 16);

// numeric(12,3) arrives as "7000.000". A number input renders that through the
// browser's locale, so a comma-decimal locale shows "7000,000" — which reads
// as seven million. Trailing zeros go before it ever reaches the field.
const toTargetInput = (value: string | null): string =>
  value === null ? "" : String(Number(value));

type RungFormProps = {
  action: (state: FormState, formData: FormData) => Promise<FormState>;
  goalType: string;
  rung?: RungRow;
  submitLabel: string;
  pendingLabel: string;
  onDone?: () => void;
};

const RungForm = ({
  action,
  goalType,
  rung,
  submitLabel,
  pendingLabel,
}: RungFormProps) => {
  const [state, formAction, isPending] = useActionState(
    action,
    initialFormState,
  );
  const shape = shapeFor(goalType);

  return (
    <form action={formAction} className={styles.form}>
      {shape.hasTarget ? (
        <TextField
          name="targetValue"
          label="Target"
          hint="The cumulative total on a plain rung; the per-day total once required days is set."
          type="number"
          defaultValue={
            state.values.targetValue ?? toTargetInput(rung?.targetValue ?? null)
          }
          errors={state.fieldErrors.targetValue}
        />
      ) : null}
      <TextField
        name="requiredDays"
        label="Required days"
        hint="Blank means do it once. Otherwise the number of distinct days — not consecutive — and above one, since a one-day repetition is a plain rung."
        type="number"
        defaultValue={
          state.values.requiredDays ?? String(rung?.requiredDays ?? "")
        }
        errors={state.fieldErrors.requiredDays}
      />
      <TextField
        name="difficultyLevel"
        label="Difficulty"
        hint="1 to 10, rising with the target across the ladder. A label and a filter only — a 20000-step rung counts as one completed quest, same as calling your mom, or six rungs would be a farm."
        type="number"
        defaultValue={
          state.values.difficultyLevel ?? String(rung?.difficultyLevel ?? "")
        }
        errors={state.fieldErrors.difficultyLevel}
      />
      <SelectField
        name="effort"
        label="Effort"
        hint="Contiguous time in one sitting, never calendar span — reading a book is SHORT, the sitting rather than the three weeks. Independent of difficulty: calling your mom is easy and quick, a marathon is hard and slow."
        options={effortOptions}
        defaultValue={state.values.effort ?? rung?.effort ?? "SHORT"}
        errors={state.fieldErrors.effort}
      />
      <TextField
        name="durationDays"
        label="Duration days"
        hint="Most rungs never expire, so blank is normal — but a repeated rung needs a window, and it must be at least as long as the required days."
        type="number"
        defaultValue={
          state.values.durationDays ?? String(rung?.durationDays ?? "")
        }
        errors={state.fieldErrors.durationDays}
      />
      <TextField
        name="activeFrom"
        label="Active from"
        type="datetime-local"
        defaultValue={
          state.values.activeFrom ?? toLocalInput(rung?.activeFrom ?? null)
        }
        errors={state.fieldErrors.activeFrom}
      />
      <TextField
        name="activeTo"
        label="Active to"
        type="datetime-local"
        defaultValue={
          state.values.activeTo ?? toLocalInput(rung?.activeTo ?? null)
        }
        errors={state.fieldErrors.activeTo}
      />
      <div className={styles.formActions}>
        <Button type="submit" variant="primary" disabled={isPending}>
          {isPending ? pendingLabel : submitLabel}
        </Button>
      </div>
    </form>
  );
};

type RungLadderProps = {
  templateId: number;
  goalType: string;
  rungs: RungRow[];
  renderedAssignments: Record<number, string | null>;
  hasMetric: boolean;
  // Rung writes stay on the page, so nothing else would refetch them.
  onChanged: () => void;
};

export const RungLadder = ({
  templateId,
  goalType,
  rungs,
  renderedAssignments,
  hasMetric,
}: RungLadderProps) => {
  const activeCount = rungs.filter((rung) => rung.status === "ACTIVE").length;
  // A partial unique index caps a MANUAL template at one active rung, so the
  // add form disappears rather than offering a write that can only conflict.
  const canAdd = goalType !== "MANUAL" || activeCount === 0;

  // The metric trigger fires on the existence of any quest row, archived or
  // not, so the first rung locks the metric permanently — and a non-MANUAL
  // template with no metric can then never satisfy the publish gate. Saying so
  // before the first rung is the only moment this is still fixable.
  const isMetricAboutToLock =
    goalType !== "MANUAL" && !hasMetric && rungs.length === 0;

  return (
    <section className={styles.section}>
      <h2 className={styles.heading}>Rungs</h2>
      <p className={styles.lede}>
        Each rung binds the assignment's numbers, and the title stays the same
        across the ladder. Difficulty is a label and a filter — it never feeds
        progression, which is what keeps a six-rung ladder from being an
        exploit.
      </p>

      {rungs.length === 0 ? (
        <p className={styles.empty}>
          No rungs yet. A published template with none is unofferable and
          invisible.
        </p>
      ) : (
        <ul className={styles.list}>
          {rungs.map((rung) => (
            <li
              key={rung.id}
              className={styles.item}
              data-archived={rung.status !== "ACTIVE" || undefined}
            >
              <div className={styles.itemHead}>
                <span className={styles.itemTitle}>
                  {renderedAssignments[rung.id] ?? "—"}
                </span>
                <StatusBadge
                  label={rung.status}
                  tone={rung.status === "ACTIVE" ? "accent" : "neutral"}
                />
                {rung.status === "ACTIVE" ? (
                  <ConfirmDialog
                    triggerLabel="Archive"
                    title="Archive this rung?"
                    body={
                      <p>
                        It drops out of the offer while in-flight and completed
                        attempts keep resolving. Its difficulty and target free
                        up for a replacement.
                      </p>
                    }
                    confirmLabel="Archive"
                    pendingLabel="Archiving..."
                    isDestructive
                    confirmAction={archiveRungAction.bind(
                      null,
                      templateId,
                      rung.id,
                    )}
                  />
                ) : (
                  <ConfirmDialog
                    triggerLabel="Restore"
                    title="Restore this rung?"
                    body={
                      <p>
                        Only possible if no active rung has taken its difficulty
                        or its target since.
                      </p>
                    }
                    confirmLabel="Restore"
                    pendingLabel="Restoring..."
                    confirmAction={restoreRungAction.bind(
                      null,
                      templateId,
                      rung.id,
                    )}
                  />
                )}
              </div>
              <RungForm
                action={updateRungAction.bind(null, templateId, rung.id)}
                goalType={goalType}
                rung={rung}
                submitLabel="Save rung"
                pendingLabel="Saving..."
              />
            </li>
          ))}
        </ul>
      )}

      {isMetricAboutToLock ? (
        <p className={styles.warning} role="alert">
          Set a metric before adding the first rung. Adding one locks the metric
          for good — archiving the rung does not release it — and a non-MANUAL
          template without a metric can never be published.
        </p>
      ) : null}

      {canAdd ? (
        <div className={styles.add}>
          <h3 className={styles.subheading}>Add a rung</h3>
          <RungForm
            action={createRungAction.bind(null, templateId)}
            goalType={goalType}
            submitLabel="Add rung"
            pendingLabel="Adding..."
          />
        </div>
      ) : (
        <p className={styles.empty}>
          A MANUAL template has exactly one rung, and this one already has it.
        </p>
      )}
    </section>
  );
};
